import { useEffect, useRef } from "react";
import { Pin } from "lucide-react";


/* The few words Griot chose to put on screen. Not cards — language surfacing out of the dark:   type set straight onto the obsidian, held in a bloom of the Lab's own light, revealed word by word,
   and carried across the stage once the point has been said. */

const words = (text) => (text || "").trim().split(/\s+/);

const Words = ({ text, from = 0 }) =>
  words(text).map((w, i) => (
    <span key={`${i}-${w}`} className="note-word" style={{ animationDelay: `${from + i * 0.075}s` }}>
      {w}
      {i < words(text).length - 1 ? "\u00A0" : ""}
    </span>
  ));

/* a drawn gesture under an idea, in the Lab's accent — it writes itself in */
const Gesture = ({ accent }) => (
  <svg className="note-gesture" viewBox="0 0 260 12" preserveAspectRatio="none" aria-hidden="true">
    <path d="M2 8 C 48 2, 86 11, 132 6 S 214 1, 258 7" stroke={accent} />
  </svg>
);

const Idea = ({ n, accent }) => (
  <span className="block" data-testid="note-text">
    <span className="note-ink block font-display text-[21px] font-light italic leading-[1.18] tracking-[-0.01em] lg:text-[33px]" style={{ color: "var(--text-main)" }}>
      <Words text={n.text} />
    </span>
    <Gesture accent={accent} />
  </span>
);

const Quote = ({ n, accent }) => (
  <span className="relative block" data-testid="note-text">
    <span className="note-mark" style={{ color: accent }} aria-hidden="true">&ldquo;</span>
    <span className="note-ink relative block font-display text-[17px] font-light italic leading-[1.5] lg:text-[23px]" style={{ color: `color-mix(in srgb, ${accent} 30%, var(--text-main))` }}>
      <Words text={n.text} />
    </span>
    <span className="note-horizon mt-2.5 block" style={{ "--note-accent": accent }} />
  </span>
);

const Name = ({ n, accent }) => (
  <span className="flex items-center gap-2.5" data-testid="note-text">
    <span className="note-dot" style={{ background: accent }} aria-hidden="true" />
    <span className="note-ink font-mono text-[11px] uppercase tracking-[0.3em] lg:text-[12.5px]" style={{ color: `color-mix(in srgb, ${accent} 55%, var(--text-main))` }}>
      <Words text={n.text} />
    </span>
  </span>
);

const Step = ({ n, accent }) => (
  <span className="note-step relative flex items-baseline gap-3.5 pl-4" style={{ "--note-accent": accent }} data-testid="note-text">
    <span className="note-ordinal font-mono text-[10px] tabular-nums tracking-[0.18em]" style={{ color: accent }}>
      {String(n.index).padStart(2, "0")}
    </span>
    <span className="note-ink block font-display text-[17px] font-light leading-[1.26] lg:text-[23px]" style={{ color: "var(--text-main)" }}>
      <Words text={n.text} from={0.1} />
    </span>
  </span>
);

const FORMS = { idea: Idea, quote: Quote, name: Name, step: Step };

/* THE SPOOL — how the words live on a phone. Not labels that float and vanish: a column that runs
   down the page, everything from the sitting still in it, newest arriving at the foot. Tap one to
   keep it, and it is still there at the end. */
export const Spool = ({ notes, accent, kept = new Set(), onKeep }) => {
  const box = useRef(null);
  const count = notes.length;
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 180;
    if (near) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });   // don't yank them back while they read
  }, [count]);

  return (
    <div className="relative z-10 flex min-h-0 flex-1 flex-col lg:hidden" data-testid="flow-notes">
      <div className="flex shrink-0 items-center justify-between px-6 pb-1.5 pt-1">
        <span className="font-mono text-[8.5px] uppercase tracking-[0.22em]" style={{ color: "var(--text-faint)" }}>
          on the table · {count}
        </span>
        {kept.size > 0 && (
          <span className="font-mono text-[8.5px] uppercase tracking-[0.22em]" style={{ color: accent }} data-testid="spool-kept-count">
            {kept.size} kept
          </span>
        )}
      </div>
      <div ref={box} className="spool-scroll min-h-0 flex-1 overflow-y-auto px-6 pb-2" data-testid="spool-scroll">
        <div className="relative flex min-h-full flex-col justify-end pl-4">
          <span className="spool-rule absolute bottom-3 left-0 top-3 w-px" style={{ "--note-accent": accent }} aria-hidden="true" />
          {notes.map((n, i) => {
            const held = kept.has(n.id);
            return (
              <button
                key={n.id}
                onClick={() => onKeep?.(n.id)}
                aria-pressed={held}
                aria-label={held ? "Let this one go" : "Keep this one"}
                data-testid="spool-note"
                className="spool-entry block w-full py-2 text-left"
                style={{ "--note-accent": accent, animationDelay: `${Math.min(i, 2) * 0.06}s` }}
              >
                <span className={`spool-card relative block rounded-[16px] px-4 py-3.5 ${held ? "is-kept" : ""}`}>
                  <span className="spool-dot" aria-hidden="true" />
                  <Piece n={n} accent={accent} align="left" />
                  {held && (
                    <span className="mt-2 flex items-center gap-1 font-mono text-[7.5px] uppercase tracking-[0.2em]" style={{ color: accent }}>
                      <Pin size={8} /> kept
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Piece = ({ n, accent, align }) => {
  const Form = FORMS[n.kind] || Idea;
  return (
    <span
      data-testid="flow-note"
      data-kind={n.kind}
      className="note-piece relative block"
      style={{ "--note-accent": accent, textAlign: align }}
    >
      <span className="note-bloom" aria-hidden="true" />
      <span className="note-vessel relative block">
        <Form n={n} accent={accent} />
      </span>
    </span>
  );
};

export const Notes = ({ notes, accent, motion = true }) => {
  if (!notes.length) return null;
  // a group of two or more is a set: numbered, read as a list
  const sizes = notes.reduce((m, n) => ({ ...m, [n.group]: (m[n.group] || 0) + 1 }), {});
  const shape = (n) => (sizes[n.group] > 1 && ["idea", "step"].includes(n.kind) ? { ...n, kind: "step" } : n);
  notes = notes.map(shape);
  const still = motion ? "" : "notes-still";

  return (
    <>

      {/* desktop: pieces either side of the breathing form, each crossing the stage once said */}
      <span className={`pointer-events-none absolute inset-0 z-0 hidden lg:block ${still}`} data-testid="flow-notes-desktop">
        {notes.map((n, i) => {
          const left = !!n.seat;   // whichever side it sits on, it crosses to the other one
          return (
            <span
              key={n.id}
              className={`note-drift absolute block ${n.leaving ? "note-leaving" : ""}`}
              data-leaving={n.leaving ? "1" : "0"}
              style={{
                "--tilt": n.kind === "step" ? "0deg" : n.seat ? "0.9deg" : "-1.1deg",
                "--fly-x": left ? "-52vw" : "52vw",
                "--fly-rot": left ? "-7deg" : "7deg",
                [n.seat ? "right" : "left"]: n.kind === "step" ? "7%" : `${6 + i * 1.5}%`,
                top: n.kind === "step" ? `${26 + (n.index - 1) * 11}%` : `${28 + i * 17}%`,
                width: n.kind === "step" ? "20rem" : "19rem",
              }}
            >
              <Piece n={n} accent={accent} align="left" />
            </span>
          );
        })}
      </span>
    </>
  );
};
